import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  useListRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  RoleDto,
} from "@/store/apiSlice";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Pencil } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

interface RoleRow {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
}

export function TeamsContent() {
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newActive, setNewActive] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const { data: rolesData, isLoading } = useListRolesQuery();
  const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();

  useEffect(() => {
    if (!rolesData) return;
    const rows = (rolesData as RoleDto[]).map((r) => ({
      id: r.id,
      code: r.code ?? "",
      name: r.name ?? "",
      is_active: r.is_active ?? true,
    }));
    setRoles(rows);
  }, [rolesData]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createRole({
        name: newName.trim(),
        code:
          newCode.trim() ||
          newName.trim().toLowerCase().replace(/\s+/g, "_"),
        is_active: newActive,
      }).unwrap();
      setNewName("");
      setNewCode("");
      setNewActive(true);
      toast.success("Team created.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to create team.");
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editName.trim()) {
      toast.error("Team name is required.");
      return;
    }
    try {
      await updateRole({
        id,
        name: editName.trim(),
        code: editCode.trim(),
        is_active: editActive,
      }).unwrap();
      toast.success("Team updated.");
      setIsEditOpen(false);
      setEditingId(null);
    } catch (e) {
      console.error(e);
      toast.error("Failed to update team.");
    }
  };

  const filteredRoles =
    statusFilter === "all"
      ? roles
      : roles.filter((r) => (statusFilter === "active" ? r.is_active : !r.is_active));

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-foreground">Teams</h2>
          <p className="text-sm text-muted-foreground">
            Manage logical teams used across the platform.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setIsCreateOpen(true)}>
          + Create Team
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-foreground">Existing teams</h3>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant={statusFilter === "all" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setStatusFilter("all")}
              >
                All
              </Button>
              <Button
                type="button"
                variant={statusFilter === "active" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setStatusFilter("active")}
              >
                Active
              </Button>
              <Button
                type="button"
                variant={statusFilter === "inactive" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setStatusFilter("inactive")}
              >
                Inactive
              </Button>
            </div>
          </div>
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Loading teams...</p>
          ) : filteredRoles.length === 0 ? (
            <p className="text-xs text-muted-foreground">No teams found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="text-xs text-muted-foreground">
                      {role.id}
                    </TableCell>
                    <TableCell className="font-medium">
                      {role.name || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {role.code || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={role.is_active ? "secondary" : "outline"}
                        className={role.is_active ? "bg-emerald-500/10 text-emerald-600" : ""}
                      >
                        {role.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setEditingId(role.id);
                          setEditName(role.name);
                          setEditCode(role.code);
                          setIsEditOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit team modal */}
      <Dialog
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open);
          if (!open) {
            setEditingId(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Team name</p>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="e.g. RED TEAM"
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Code (optional)</p>
              <Input
                value={editCode}
                onChange={(e) => setEditCode(e.target.value)}
                placeholder="e.g. red_team"
              />
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-xs text-muted-foreground">Active status</span>
              <Switch
                checked={editActive}
                onCheckedChange={(checked: boolean) => setEditActive(checked)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setIsEditOpen(false);
                setEditingId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isUpdating || !editingId}
              onClick={() => {
                if (!editingId) return;
                void handleUpdate(editingId);
              }}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create team modal */}
      <Dialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) {
            setNewName("");
            setNewCode("");
            setNewActive(true);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Team name</p>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. RED TEAM"
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Code (optional)</p>
              <Input
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                placeholder="e.g. red_team"
              />
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-xs text-muted-foreground">Active status</span>
              <Switch
                checked={newActive}
                onCheckedChange={(checked: boolean) => setNewActive(checked)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setIsCreateOpen(false);
                setNewName("");
                setNewCode("");
                setNewActive(true);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!newName.trim() || isCreating}
              onClick={async () => {
                await handleCreate();
                setIsCreateOpen(false);
              }}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

